/**
 * Dashboard Page - Main home screen
 * Shows statistics and recent projects
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FolderOpen, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { projectsApi } from '../lib/api';
import { StatsCard } from '../components/dashboard/StatsCard';
import { RecentProjects } from '../components/dashboard/RecentProjects';

interface Project {
  id: string;
  code: string;
  name: string;
  line?: string;
  status: string;
  progress: number;
  photoCount: number;
  anomalyCount: number;
  elementCount: number;
  surveyDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface DashboardStats {
  totalProjects: number;
  inProgress: number;
  completed: number;
  totalPhotos: number;
  totalAnomalies: number;
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load all projects from API
      const response = await projectsApi.list({ limit: 1000 });
      const projects = response.data?.data;  // data is array directly, not data.projects
      
      if (!projects || !Array.isArray(projects)) {
        // No data or not logged in - set empty state
        setStats({
          totalProjects: 0,
          inProgress: 0,
          completed: 0,
          totalPhotos: 0,
          totalAnomalies: 0,
        });
        setRecentProjects([]);
        return;
      }
      
      // Calculate stats
      const dashboardStats: DashboardStats = {
        totalProjects: projects.length,
        inProgress: projects.filter(p => p.status === 'IN_PROGRESS').length,
        completed: projects.filter(p => p.status === 'COMPLETED' || p.status === 'EXPORTED').length,
        totalPhotos: projects.reduce((sum, p) => sum + (p.photoCount || 0), 0),
        totalAnomalies: projects.reduce((sum, p) => sum + (p.anomalyCount || 0), 0),
      };
      setStats(dashboardStats);

      // Get recent projects (last 4) - sorted by updatedAt
      const recent = [...projects]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 4);
      setRecentProjects(recent);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Set empty state on error
      setStats({
        totalProjects: 0,
        inProgress: 0,
        completed: 0,
        totalPhotos: 0,
        totalAnomalies: 0,
      });
      setRecentProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const today = format(new Date(), "yyyy.MM.dd // HH:mm:ss");
  const dayOfWeek = format(new Date(), "EEEE").toUpperCase();

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-slate-300 dark:bg-slate-700/50 rounded w-64 mb-2" />
        <div className="h-5 bg-slate-300 dark:bg-slate-700/50 rounded w-48 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-300 dark:bg-slate-700/50 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-full">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100 tracking-wide">
          GRIDINSPECT <span className="text-blue-600 dark:text-blue-400">DASHBOARD</span>
        </h1>
        <p className="text-slate-500 font-mono text-sm mt-1">
          {dayOfWeek} :: {today}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatsCard
          title="Total Projects"
          value={stats?.totalProjects || 0}
          icon={FolderOpen}
          color="blue"
        />
        <StatsCard
          title="In Progress"
          value={stats?.inProgress || 0}
          icon={Clock}
          color="amber"
        />
        <StatsCard
          title="Completed"
          value={stats?.completed || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatsCard
          title="Total Anomalies"
          value={stats?.totalAnomalies || 0}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Recent Projects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-header text-slate-500 dark:text-slate-400">// Recent Projects</h2>
          <Link 
            to="/projects" 
            className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 text-xs font-mono font-medium flex items-center gap-1 uppercase tracking-wide"
          >
            View all
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <RecentProjects projects={recentProjects as any} />
      </div>
    </div>
  );
}
